import type { CatalogItem } from "@/lib/airtable";

export type Affinity = Record<string, number>;

export type PreferenceState = {
  type: Affinity;
  metal: Affinity;
  stone: Affinity;
};

export type InitialPreferences = {
  types?: string[];
  metals?: string[];
  stones?: string[];
};

// A point-in-time snapshot of deck progress, used to power one-step undo.
export type DeckSnapshot = {
  prefs: PreferenceState;
  seenIds: Set<string>;
  currentId: string | null;
  liked: CatalogItem[];
  disliked: CatalogItem[];
};

export type SwipeState = DeckSnapshot & {
  // Snapshots captured immediately BEFORE each like/dislike, newest last.
  history: DeckSnapshot[];
  // Onboarding-derived preferences, preserved so `reset` returns to the user's
  // original taste rather than the affinities learned mid-session.
  initialPrefs: PreferenceState;
};

export type SwipeAction =
  | { type: "like" }
  | { type: "dislike" }
  | { type: "undo" }
  | { type: "reset" };

// ─── Config ─────────────────────────────────────────────────────────────────

const INITIAL_PREFERENCE_WEIGHT = 10;
const LIKE_DELTA = 2;
const DISLIKE_DELTA = -2;
const DISLIKE_FLOOR = -5;
const TYPE_MULTIPLIER = 3;
const METAL_MULTIPLIER = 3;
const STONE_MULTIPLIER = 2;

// ─── Case-insensitive key normalization ─────────────────────────────────────
// All affinity keys and item field values are lowercased before comparison
// to prevent mismatches between onboarding labels and Airtable data.

const norm = (value: string) => value.toLowerCase().trim();

const createInitialPrefs = (initial?: InitialPreferences): PreferenceState => {
  const prefs: PreferenceState = { type: {}, metal: {}, stone: {} };
  if (initial?.types) {
    for (const t of initial.types) prefs.type[norm(t)] = INITIAL_PREFERENCE_WEIGHT;
  }
  if (initial?.metals) {
    for (const m of initial.metals) prefs.metal[norm(m)] = INITIAL_PREFERENCE_WEIGHT;
  }
  if (initial?.stones) {
    for (const s of initial.stones) prefs.stone[norm(s)] = INITIAL_PREFERENCE_WEIGHT;
  }
  return prefs;
};

const clampMin = (value: number, min: number) => (value < min ? min : value);

// ─── Scoring ────────────────────────────────────────────────────────────────
// Uses case-insensitive word-boundary matching so "Ring" in prefs matches
// "ring", "Ring", and also "Engagement Ring" or "Cocktail Ring" in item.type.

const getAffinityScore = (affinity: Affinity, fieldValue: string): number => {
  const normalizedField = norm(fieldValue);
  if (!normalizedField) return 0;

  // Direct match first (fast path)
  if (affinity[normalizedField] !== undefined) {
    return affinity[normalizedField];
  }

  // Word-boundary match: check if any affinity key appears as a word in the field
  for (const [key, score] of Object.entries(affinity)) {
    const re = new RegExp(`\\b${key}\\b`, "i");
    if (re.test(normalizedField)) {
      return score;
    }
  }

  return 0;
};

const computeScore = (item: CatalogItem, prefs: PreferenceState) => {
  const typeScore = getAffinityScore(prefs.type, item.type);
  const metalScore = getAffinityScore(prefs.metal, item.metal);
  const stoneScore = getAffinityScore(prefs.stone, item.stone);
  return typeScore * TYPE_MULTIPLIER + metalScore * METAL_MULTIPLIER + stoneScore * STONE_MULTIPLIER;
};

const hasAnyPreference = (prefs: PreferenceState): boolean => {
  return (
    Object.values(prefs.type).some((v) => v > 0) ||
    Object.values(prefs.metal).some((v) => v > 0) ||
    Object.values(prefs.stone).some((v) => v > 0)
  );
};

// ─── Check if item matches ANY positive type preference ─────────────────────

const matchesPreferredType = (item: CatalogItem, typePrefs: Affinity): boolean => {
  const positiveKeys = Object.entries(typePrefs).filter(([, v]) => v > 0).map(([k]) => k);
  if (positiveKeys.length === 0) return true; // no type preference → everything matches
  const normalizedType = norm(item.type);
  if (!normalizedType) return false;
  return positiveKeys.some((key) => {
    const re = new RegExp(`\\b${key}\\b`, "i");
    return re.test(normalizedType);
  });
};

const hasTypePreference = (prefs: PreferenceState): boolean => {
  return Object.values(prefs.type).some((v) => v > 0);
};

// ─── Preference updates ─────────────────────────────────────────────────────

const updatePrefs = (prefs: PreferenceState, item: CatalogItem, delta: number) => {
  const next: PreferenceState = {
    type: { ...prefs.type },
    metal: { ...prefs.metal },
    stone: { ...prefs.stone }
  };

  const applyDelta = (group: Affinity, key: string) => {
    if (!key) return;
    const normalizedKey = norm(key);
    const previous = group[normalizedKey] ?? 0;
    const updated = delta < 0 ? clampMin(previous + delta, DISLIKE_FLOOR) : previous + delta;
    group[normalizedKey] = updated;
  };

  applyDelta(next.type, item.type);
  applyDelta(next.metal, item.metal);
  applyDelta(next.stone, item.stone);

  return next;
};

// ─── Selection ──────────────────────────────────────────────────────────────

export const pickNext = (
  items: CatalogItem[],
  prefs: PreferenceState,
  seen: Set<string>
): CatalogItem | null => {
  const unseen = items.filter((item) => !seen.has(item.id));
  if (unseen.length === 0) return null;

  if (!hasAnyPreference(prefs)) {
    // No preferences set — return items in catalog order
    return unseen[0];
  }

  // If the user selected specific types, FILTER to those types first.
  // Only show non-matching types once all preferred items are exhausted.
  let candidates = unseen;
  if (hasTypePreference(prefs)) {
    const preferred = unseen.filter((item) => matchesPreferredType(item, prefs.type));
    if (preferred.length > 0) {
      candidates = preferred;
    }
    // If no preferred items remain, fall back to all unseen items
  }

  // Score all candidates
  const scored = candidates.map((item) => ({
    item,
    score: computeScore(item, prefs)
  }));

  // Sort by score descending, then by item name for deterministic order within ties
  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.item.itemName.localeCompare(b.item.itemName);
  });

  return scored[0].item;
};

// ─── State transitions ────────────────────────────────────────────────────────

export const createInitialState = (
  items: CatalogItem[],
  initialPreferences?: InitialPreferences
): SwipeState => {
  const prefs = createInitialPrefs(initialPreferences);
  const seenIds = new Set<string>();
  const next = pickNext(items, prefs, seenIds);
  return {
    prefs,
    seenIds,
    currentId: next ? next.id : null,
    liked: [],
    disliked: [],
    history: [],
    initialPrefs: prefs
  };
};

const snapshot = (state: SwipeState): DeckSnapshot => ({
  prefs: state.prefs,
  seenIds: state.seenIds,
  currentId: state.currentId,
  liked: state.liked,
  disliked: state.disliked
});

const advance = (state: SwipeState, items: CatalogItem[], delta: number): SwipeState => {
  if (!state.currentId) return state;
  const item = items.find((entry) => entry.id === state.currentId);
  if (!item) return state;

  const nextPrefs = updatePrefs(state.prefs, item, delta);
  const nextSeen = new Set(state.seenIds);
  nextSeen.add(item.id);
  const nextItem = pickNext(items, nextPrefs, nextSeen);

  return {
    prefs: nextPrefs,
    seenIds: nextSeen,
    currentId: nextItem ? nextItem.id : null,
    liked: delta > 0 ? [...state.liked, item] : state.liked,
    disliked: delta < 0 ? [...state.disliked, item] : state.disliked,
    history: [...state.history, snapshot(state)],
    initialPrefs: state.initialPrefs
  };
};

export const reduce = (
  state: SwipeState,
  action: SwipeAction,
  items: CatalogItem[]
): SwipeState => {
  switch (action.type) {
    case "like":
      return advance(state, items, LIKE_DELTA);
    case "dislike":
      return advance(state, items, DISLIKE_DELTA);
    case "undo": {
      if (state.history.length === 0) return state;
      const previous = state.history[state.history.length - 1];
      return { ...previous, history: state.history.slice(0, -1), initialPrefs: state.initialPrefs };
    }
    case "reset": {
      const seenIds = new Set<string>();
      const next = pickNext(items, state.initialPrefs, seenIds);
      return {
        prefs: state.initialPrefs,
        seenIds,
        currentId: next ? next.id : null,
        liked: [],
        disliked: [],
        history: [],
        initialPrefs: state.initialPrefs
      };
    }
    default:
      return state;
  }
};

// ─── Selectors ──────────────────────────────────────────────────────────────

export const selectCurrentItem = (state: SwipeState, items: CatalogItem[]): CatalogItem | null =>
  items.find((item) => item.id === state.currentId) ?? null;

export const selectRemainingCount = (state: SwipeState, items: CatalogItem[]): number => {
  const unseen = items.length - state.seenIds.size - (state.currentId ? 1 : 0);
  return unseen > 0 ? unseen : 0;
};

export const canUndo = (state: SwipeState): boolean => state.history.length > 0;
